"use client"

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Tabs as TabsPrimitive } from "radix-ui"

import { cn } from "../../../lib/utils"

type TabsListVariant = "default" | "line"
const TabsListVariantContext = React.createContext<TabsListVariant>("default")

function Tabs({
  className,
  orientation = "horizontal",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn(
        "group/tabs flex gap-2 data-[orientation=horizontal]:flex-col",
        className
      )}
      {...props}
    />
  )
}

/**
 * Tabs — DESIGN.md `tab-*`:
 * - `line` variant (preferred): inactive gray-500, active brand-blue with
 *   2px brand-blue underline at the bottom. 13px / 400 weight.
 * - `default` variant: kept for legacy pill-tab usage.
 */
const tabsListVariants = cva(
  "group/tabs-list relative inline-flex w-fit items-stretch justify-center text-muted-foreground group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none",
  {
    variants: {
      variant: {
        default: "bg-background border border-border p-1 group-data-[orientation=horizontal]/tabs:h-10 group-data-[orientation=horizontal]/tabs:rounded-md group-data-[orientation=vertical]/tabs:rounded-md",
        line: "gap-0 bg-transparent border-b border-border group-data-[orientation=horizontal]/tabs:h-9",
      },
    },
    defaultVariants: {
      variant: "line",
    },
  }
)

function useActiveIndicator(listRef: React.RefObject<HTMLElement | null>) {
  const [indicator, setIndicator] = React.useState({ left: 0, top: 0, width: 0, height: 0, ready: false })

  const update = React.useCallback(() => {
    const list = listRef.current
    if (!list) return
    const activeEl = list.querySelector<HTMLElement>('[data-state="active"]')
    if (!activeEl) return
    const next = {
      left: activeEl.offsetLeft,
      top: activeEl.offsetTop,
      width: activeEl.offsetWidth,
      height: activeEl.offsetHeight,
      ready: true,
    }
    setIndicator(prev =>
      prev.left === next.left &&
      prev.top === next.top &&
      prev.width === next.width &&
      prev.height === next.height &&
      prev.ready === next.ready
        ? prev
        : next
    )
  }, [listRef])

  React.useEffect(() => {
    update()

    const list = listRef.current
    if (!list) return

    const observer = new MutationObserver(update)
    observer.observe(list, { attributes: true, subtree: true, attributeFilter: ["data-state"] })
    window.addEventListener("resize", update)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [update, listRef])

  return indicator
}

interface TabOption {
  value: string
  label: React.ReactNode
  badge?: React.ReactNode
}

function TabsList({
  className,
  variant = "default",
  options,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants> & {
    options?: TabOption[]
  }) {
  // Attach the ref to a plain div instead of TabsPrimitive.List to avoid the
  // radix-ui/primitives compose-refs bug with React 19 (radix-ui/primitives#3799).
  // `display: contents` keeps the wrapper transparent to CSS layout.
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const indicator = useActiveIndicator(wrapperRef)
  const resolvedVariant = variant ?? "default"

  return (
    <TabsListVariantContext value={resolvedVariant}>
      <div ref={wrapperRef} style={{ display: "contents" }}>
        <TabsPrimitive.List
          data-slot="tabs-list"
          data-variant={variant}
          className={cn(tabsListVariants({ variant }), className)}
          {...props}
        >
          {options
            ? options.map((opt) => (
                <TabsTrigger key={opt.value} value={opt.value} badge={opt.badge}>
                  {opt.label}
                </TabsTrigger>
              ))
            : children}
          {resolvedVariant === "line" ? (
            <div
              aria-hidden
              className="absolute -bottom-px h-[2px] bg-primary transition-all duration-200 ease-out"
              style={{
                left: indicator.left,
                width: indicator.width,
                opacity: indicator.ready ? 1 : 0,
              }}
            />
          ) : (
            <div
              aria-hidden
              className="absolute z-0 rounded-sm bg-primary transition-all duration-200 ease-out"
              style={{
                left: indicator.left,
                top: indicator.top,
                width: indicator.width,
                height: indicator.height,
                opacity: indicator.ready ? 1 : 0,
              }}
            />
          )}
        </TabsPrimitive.List>
      </div>
    </TabsListVariantContext>
  )
}

function TabsTrigger({
  className,
  badge,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & {
  badge?: React.ReactNode
}) {
  const listVariant = React.use(TabsListVariantContext)
  const isLine = listVariant === "line"

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "group/trigger relative z-10 inline-flex items-center justify-center gap-1.5 border border-transparent bg-transparent text-[13px] font-normal whitespace-nowrap text-muted-foreground transition-colors duration-200 group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring/60 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        isLine
          ? "px-3 py-2 rounded-none data-[state=active]:text-primary"
          : "flex-1 px-5 py-1 rounded-sm data-[state=active]:text-primary-foreground",
        className
      )}
      {...props}
    >
      {children}
      {badge != null && (
        <span
          className={cn(
            "inline-flex items-center justify-center rounded-full border border-transparent px-1.5 py-0 text-[10px] font-medium transition-colors duration-300 bg-foreground/10 text-muted-foreground",
            isLine
              ? "group-data-[state=active]/trigger:bg-primary group-data-[state=active]/trigger:text-primary-foreground"
              : "group-data-[state=active]/trigger:bg-primary-foreground group-data-[state=active]/trigger:text-primary",
          )}
        >
          {badge}
        </span>
      )}
    </TabsPrimitive.Trigger>
  )
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants }
export type { TabOption }
